import { COMPARE_LABEL, P } from "./constants";
import { fmt } from "./helpers";
import { DateTime } from "luxon";

export const SEGMENT_COLORS = [P.nv, "#241C8A", "#3127B8", "#3EAC4D", "#666666"];

export const METRIC_DEFINITIONS = [
  {
    term: "Hotel net revenue",
    text: "Gross parking charges minus refunds and DRIVO fees, GST-inclusive. Not profit or a bank settlement amount.",
  },
  {
    term: "Revenue leakage (estimated)",
    text: "Estimated uncollected revenue from free-of-charge exits, buzz-outs, front desk overrides and overstays.",
  },
  {
    term: "Parking occupancy",
    text: "Average occupied parking capacity for the selected dates.",
  },
  {
    term: "DRIVO charge rate",
    text: "Share of bookings processed through DRIVO at exit.",
  },
];

export function getOverviewCompareLabel(period, fallback) {
  const labels = {
    custom: "prior equivalent",
    TODAY: "yesterday",
    YESTERDAY: "previous day",
    day: "previous day",
    YTD: "same period last year",
  };

  return labels[period] || COMPARE_LABEL[period] || fallback;
}

export function getBudgetGoal(monthlyBudget, dayCount) {
  return Math.round((monthlyBudget / 30) * Math.max(1, dayCount || 1));
}

const exactMoney = value => new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
}).format(Math.round(value));

const exactAud = value => new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value);

export function buildOverviewViewModel(data, compareLabel, budgetGoal) {
  const achievedRevenue = data.budget.achievedRevenue;
  const revenueChart = data.revenueChart;
  const pacePercent = budgetGoal > 0 ? Math.round((achievedRevenue / budgetGoal) * 100) : 0;
  const paceGap = achievedRevenue - budgetGoal;
  const tolerance = Math.max(1, budgetGoal) * 0.03;
  const paceState = Math.abs(paceGap) <= tolerance
    ? "on"
    : paceGap > 0 ? "ahead" : "behind";
  const paceStatus = paceState === "on"
    ? "On pace"
    : `${paceState === "ahead" ? "Ahead of" : "Behind"} pace by ${exactMoney(Math.abs(paceGap))}`;

  return {
    data,
    compareLabel,
    achieved: achievedRevenue,
    budgetGoal,
    pacePercent,
    paceState,
    paceStatus,
    remainingBudget: Math.max(0, budgetGoal - achievedRevenue),
    revenueChart,
    refundTotal: revenueChart.reduce((sum, point) => sum + point.refunds, 0),
    previousPeriodRevenue: data.revenueChart.reduce((sum, point) => sum + point.previousHotelNetRevenue, 0),
    actions: [
      ...(paceState === "behind" ? [{
        tag: "Revenue",
        title: `Hotel net revenue is ${exactMoney(Math.abs(paceGap))} behind budget pace`,
        detail: `${fmt(achievedRevenue)} earned against a ${fmt(budgetGoal)} pace target for this selection.`,
        label: "Review channels",
        tab: "channels",
      }] : []),
      ...(data.leakage ? [{
        tag: "Leakage",
        title: `${fmt(data.leakage.totalAmount)} of revenue leaked in ${data.currentPeriodLabel}`,
        detail: `Estimated losses from recorded incidents, equal to ${data.leakage.percentOfGrossRevenue}% of gross charges.`,
        label: "Review leakage",
        tab: "leakage",
      }, {
        tag: "Buzz-outs",
        title: `${data.leakage.buzzOutCount} buzz-outs cost ${fmt(data.leakage.buzzOutAmount)}`,
        detail: null,
        label: "See buzz-outs",
        tab: "leakage",
      }] : []),
    ],
  };
}

export function buildRevenueLeakageViewModel(
  response,
  buzzOutsPerDay,
  focCardsPerDay,
) {
  const byType = Object.fromEntries(response.items.map((item) => [item.type, item]));
  const row = (type, label, limitPerDay, amountOverride) => {
    const item = byType[type] || { amount: 0, count: 0, averagePerDay: 0 };
    return {
      type,
      label,
      count: item.count,
      amount: amountOverride ?? item.amount,
      averagePerDay: item.averagePerDay,
      limitPerDay,
      progress: limitPerDay == null
        ? null
        : Math.min(100, (item.averagePerDay / Math.max(limitPerDay, 0.01)) * 100),
    };
  };

  return {
    startDate: response.startDate,
    endDate: response.endDate,
    dayCount: response.dayCount,
    rows: [
      row("BUZZ_OUT", "Buzz-outs", buzzOutsPerDay),
      row("FOC_GUEST_CARD", "FOC guest cards", focCardsPerDay, 0),
      row("FAILED_STRIPE_CHARGE", "Failed Stripe charges", null),
    ],
  };
}

export function buildRevenueSegmentsViewModel(response, totalHotelNetRevenue) {
  return response.items.map((item) => ({
    id: item.rateCardId,
    name: item.name,
    revenue: item.revenue,
    share: totalHotelNetRevenue > 0
      ? Math.round((item.revenue / totalHotelNetRevenue) * 1000) / 10
      : 0,
  }));
}

function occupancyChange(current, previous) {
  if (!previous) return current ? 100 : 0;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

export function buildOccupancyViewModel(response) {
  return {
    startDate: response.startDate,
    endDate: response.endDate,
    previousStartDate: response.previousStartDate,
    previousEndDate: response.previousEndDate,
    granularity: response.granularity,
    parkingOccupancyPercent: response.parkingOccupancyPercent,
    hotelOccupancyPercent: response.hotelOccupancyPercent,
    drivoChargeRatePercent: response.drivoChargeRatePercent,
    previousDrivoChargeRatePercent: response.previousDrivoChargeRatePercent,
    parkingDelta: occupancyChange(
      response.parkingOccupancyPercent,
      response.previousParkingOccupancyPercent,
    ),
    hotelDelta: occupancyChange(
      response.hotelOccupancyPercent,
      response.previousHotelOccupancyPercent,
    ),
    points: response.points.map((point) => ({
      label: point.label,
      parking: point.parkingOccupancyPercent,
      hotel: point.hotelOccupancyPercent,
      previousParking: point.previousParkingOccupancyPercent,
      previousHotel: point.previousHotelOccupancyPercent,
    })),
  };
}

function percentChange(current, previous) {
  if (!previous) return current ? 100 : 0;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

function formatDateRange(period) {
  const start = DateTime.fromISO(period.startDate);
  const end = DateTime.fromISO(period.endDate).minus({ days: 1 });
  if (!start.isValid || !end.isValid) return `${period.startDate} to ${period.endDate}`;
  if (start.hasSame(end, "day")) return start.toFormat("d LLL yyyy");
  if (start.month === end.month && start.year === end.year) {
    return `${start.day} to ${end.toFormat("d LLL yyyy")}`;
  }
  return `${start.toFormat("d LLL yyyy")} to ${end.toFormat("d LLL yyyy")}`;
}

export function overviewResponseToData(response, revenueLeakage, previousRevenueLeakage, occupancy) {
  const current = response.currentPeriod;
  const previous = response.previousPeriod;
  const currentMetrics = current.metrics;
  const previousMetrics = previous.metrics;
  const metric = (label, value, previousValue, suffix = "", formatValue = fmt) => {
    const delta = percentChange(value, previousValue);
    return {
      label,
      displayValue: suffix ? `${value}${suffix}` : formatValue(value),
      percentChange: delta,
      increased: delta >= 0,
    };
  };
  const previousDailyPoints = previous.daily;
  const leakageItems = revenueLeakage?.items || [];
  const leakageByType = Object.fromEntries(leakageItems.map((item) => [item.type, item]));
  const leakageItemFor = (type) => leakageByType[type] || { amount: 0, count: 0 };
  const totalLeakage = leakageItems.reduce((sum, item) => sum + item.amount, 0);
  const previousTotalLeakage = previousRevenueLeakage?.items.reduce(
    (sum, item) => sum + item.amount, 0,
  );
  const leakagePercentChange = previousTotalLeakage == null
    ? null
    : percentChange(totalLeakage, previousTotalLeakage);

  return {
    currentPeriodLabel: formatDateRange(current),
    compareLabel: formatDateRange(previous),
    metrics: {
      hotelNetRevenue: metric("Hotel net revenue", currentMetrics.hotelNetRevenue, previousMetrics.hotelNetRevenue, "", exactAud),
      parkingOccupancy: occupancy ? metric("Parking occupancy", occupancy.parkingOccupancyPercent, occupancy.previousParkingOccupancyPercent, "%") : null,
      hotelOccupancy: occupancy ? metric("Hotel occupancy", occupancy.hotelOccupancyPercent, occupancy.previousHotelOccupancyPercent, "%") : null,
      drivoChargeRate: occupancy ? metric("DRIVO charge rate", occupancy.drivoChargeRatePercent, occupancy.previousDrivoChargeRatePercent, "%") : null,
    },
    revenueChart: current.daily.map((point, index) => {
      const previousPoint = previousDailyPoints[index] || {};
      return {
        dateLabel: point.date.slice(5),
        hotelNetRevenue: point.hotelNetRevenue,
        refunds: point.refunds,
        previousHotelNetRevenue: previousPoint.hotelNetRevenue || 0,
      };
    }),
    budget: { achievedRevenue: currentMetrics.hotelNetRevenue },
    leakage: revenueLeakage ? {
      totalAmount: totalLeakage,
      percentChange: leakagePercentChange,
      increased: leakagePercentChange != null ? leakagePercentChange >= 0 : null,
      percentOfGrossRevenue: currentMetrics.grossParkingRevenue
        ? Math.round((totalLeakage / currentMetrics.grossParkingRevenue) * 1000) / 10
        : 0,
      frontDeskOverrideAmount: leakageItemFor("FRONT_DESK_OVERRIDE").amount,
      frontDeskOverrideCount: leakageItemFor("FRONT_DESK_OVERRIDE").count,
      buzzOutAmount: leakageItemFor("BUZZ_OUT").amount,
      buzzOutCount: leakageItemFor("BUZZ_OUT").count,
      focGuestCardAmount: leakageItemFor("FOC_GUEST_CARD").amount,
      focGuestCardCount: leakageItemFor("FOC_GUEST_CARD").count,
      overstayAmount: leakageItemFor("OVERSTAY").amount,
      overstayCount: leakageItemFor("OVERSTAY").count,
      failedStripeChargeAmount: leakageItemFor("FAILED_STRIPE_CHARGE").amount,
      failedStripeChargeCount: leakageItemFor("FAILED_STRIPE_CHARGE").count,
    } : null,
  };
}
